import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Пикер пользователей Active Directory. Запрашивает GET /api/ad/users?q=...
 * (бэк делает LDAP-search). Возвращает в onChange объект:
 *   { upn, dn, displayName, sam, mail }
 *
 * value: object | null (тот же формат). Если переданы только upn/dn — рендер ок.
 * defaultName — предзаполнение поиска (например, ФИО сотрудника).
 */
export default function AdUserPicker({ value, onChange, defaultName, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSearch(defaultName || '');
  }, [open, defaultName]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const data = await api.get(`/api/ad/users?q=${encodeURIComponent(search)}&limit=50`);
        setResults(data.rows || []);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
        setResults([]);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open]);

  const display = value && (value.displayName || value.upn || value.dn);

  return (
    <div className="ad-picker" ref={ref}>
      <div className="ad-picker__row">
        <button
          type="button"
          className="ad-picker__current"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          {value ? (
            <span className="ad-picker__value">
              <span className="ad-picker__name">{display}</span>
              {value.upn && <span className="mono ad-picker__upn">{value.upn}</span>}
            </span>
          ) : <span className="ad-picker__empty">Выберите пользователя AD…</span>}
          <Icon name="chevronDown" size={10} />
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="ad-picker__clear"
            onClick={() => onChange(null)}
            title="Очистить"
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="ad-picker__menu">
          <input
            className="ad-picker__search"
            placeholder="Поиск: ФИО, sAMAccountName, UPN, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {error && <div className="ad-picker__error">{error}</div>}
          <div className="ad-picker__list">
            {loading && <div className="ad-picker__hint">Поиск в AD…</div>}
            {!loading && !error && results.map((r) => (
              <button
                key={r.dn}
                type="button"
                className="ad-picker__item"
                onClick={() => { onChange(r); setOpen(false); }}
              >
                <span className="ad-picker__item-name">{r.displayName || r.sam || r.upn}</span>
                <span className="ad-picker__item-meta">
                  {r.upn && <span className="mono">{r.upn}</span>}
                  {r.mail && <span> · {r.mail}</span>}
                </span>
              </button>
            ))}
            {!loading && !error && results.length === 0 && (
              <div className="ad-picker__hint">
                {search ? 'Ничего не найдено' : 'Начните вводить, чтобы найти пользователя'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
